'use strict';
import classNames from 'classnames';
import get from 'lodash/get';
import * as T from 'prop-types';
import React, {Fragment} from 'react';
import {CommentIndicator} from '../../../comments/components/CommentIndicator';
import {Button} from '../../../components/buttons/Button';
import {QuickStatusButton} from '../../../components/buttons/QuickStatusButton';
import {StatefulButton} from '../../../components/buttons/StatefulButton';
import {StatelessStatusSelector} from '../../../core-components/selectors/components/StatelessStatusSelector';
import {getAsString, getCollection, getId} from '../../../core-utilities/domain/ReduxUtils';
import {ButtonState, isButtonState} from '../../../core-utilities/enums/ButtonState';
import {getMessagesFromProps, I18nComponent} from '../../../core-utilities/react-i18n';
import {TaskStatus} from '../../../tasks/enums/TaskStatus';


export function BaseRequirementViewBannerFormView(props) {
  const requirement = props.requirement;
  const description = get(requirement, 'description');
  const status = get(requirement, 'status');
  const canEdit = requirement && status !== TaskStatus.cancelled && status !== TaskStatus.approved;
  const {saveButtonDisabled, saveButtonLabel} = getSaveButtonLabel(props);
  // TODO: apply disabled={saveButtonDisabled} to save button once confirmed the isDirty tracker is reliable.

  return (
    <div className={classNames("RequirementViewBanner", props.className)}>
      <div className="RequirementViewBanner__header">
        <h1 className="RequirementViewBanner__headerLabel">
          {getAsString(requirement)}
        </h1>

        <div className="RequirementViewBanner__statusSection">
          <span className="RequirementViewBanner__statusLabel">
            {`${props.statusLabel}:`}
          </span>
          {requirement ? (
            <StatelessStatusSelector
              className="RequirementViewBanner__status"
              labelClassName="RequirementViewBanner__status__label"
              model={requirement}
              hideValue={false}
              hideIcon={true}

              status={requirement.status}
              collection={requirement.collection}
              validStatuses={requirement.validStatuses}
            />
          ) : null}
          <CommentIndicator
            className="RequirementViewBanner__chat"
            titleClassName="RequirementViewBanner__chatTitle"
            displayTitle={true}
            title={props.chatTitle}
            userId={props.userId}
            chatId={getId(requirement)}
            collection={getCollection(requirement)}
            allowNav={false}

            init={props.init}
            {...getMessagesFromProps(props, CommentIndicator.messageProps)}
          />
          <Button
            className="RequirementViewBanner__editButton"
            label={props.editLabel}
            onClick={props.onClickEdit}
          />
        </div>

        {description && (
          <Fragment>
            <span className="LabeledBlock__label">
              Description
            </span>
            <span className={classNames("RequirementViewBanner__description", {
              RequirementViewBanner__description_collapsed: !props.isHeaderExpanded,
              RequirementViewBanner__description_expanded: props.isHeaderExpanded,
            })}
            >
              {description}
            </span>
          </Fragment>
        )}

        <div className="RequirementViewBanner__wrapper">
          {description && (
            <Button
              className="RequirementViewBanner__expandButton"
              label={props.isHeaderExpanded ? props.collapseLabel : props.expandLabel}
              onClick={props.onToggleHeaderExpanded}
            />
          )}
        </div>
      </div>

      <div className="RequirementViewBanner__headerSubPanel">
        <QuickStatusButton
          className="RequirementViewBanner__quickButton"
          model={requirement}
          status={get(requirement, 'status')}
          validStatuses={get(requirement, 'validStatuses')}
        />
        <Button
          className={classNames({
            RequirementViewBannerForm__editButton: !props.isEditingForm,
            RequirementViewBannerForm__cancelButton: props.isEditingForm,
          })}
          disabled={!canEdit || props.updateFormMode !== ButtonState.START}
          label={props.isEditingForm ? props.cancelFormLabel : props.editFormLabel}
          labelClass="RequirementViewBanner__formButton__label"
          onClick={props.onToggleEditingForm}
        />
        {props.isEditingForm ? (
          <StatefulButton
            buttonClassName="FormModal__saveButton RequirementViewBannerForm__saveButton"
            startClassName="FormModal__saveButton_start"
            frontClassName="FormModal__saveButton_content"
            backClassName="FormModal__saveButton_content"
            workingIconClassName="FormModal__saveButton_icon"
            startLabel={saveButtonLabel}
            usePropState={true}
            mode={props.updateFormMode}
            onClick={props.onTriggerSaveForm}
          />
        ) : null}
      </div>
    </div>
  );
}

BaseRequirementViewBannerFormView.propTypes = {
  className: T.string,
  isHeaderExpanded: T.bool,
  userId: T.string.isRequired,
  requirement: T.object,
  showQuickSubmit: T.bool,
  showQuickApprove: T.bool,
  quickMode: isButtonState,
  isEditingForm: T.bool,
  isFormDirty: T.bool,

  onClickEdit: T.func.isRequired,
  onToggleHeaderExpanded: T.func.isRequired,
  onToggleEditingForm: T.func,
  onTriggerSaveForm: T.func,

  statusLabel: T.string,
  editLabel: T.string,
  collapseLabel: T.string,
  expandLabel: T.string,
  editFormLabel: T.string,
  cancelFormLabel: T.string,
  saveFormLabel: T.string,
  noChangesLabel: T.string,
};

BaseRequirementViewBannerFormView.defaultProps = {
  className: '',
};

export const RequirementViewBannerFormView = I18nComponent(BaseRequirementViewBannerFormView, [
  {code: 'status.label', prop: 'statusLabel'},
  {code: 'default.button.edit.label', prop: 'editLabel'},
  {code: 'default.button.collapse.label', prop: 'collapseLabel'},
  {code: 'default.button.expand.label', prop: 'expandLabel'},
  {code: 'default.edit.label', prop: 'editFormLabel'},
  {code: 'default.cancel.label', prop: 'cancelFormLabel'},
  {code: 'default.save.label', prop: 'saveFormLabel'},
  {code: 'forms.buttons.save.changes.no.label', prop: 'noChangesLabel'},
  {code: 'requirements.views.chat.title', prop: 'chatTitle'},
  ...CommentIndicator.messages,
]);

function getSaveButtonLabel(props) {
  if (!props.isFormDirty && props.updateFormMode === ButtonState.START) {
    return {
      saveButtonDisabled: true,
      saveButtonLabel: props.noChangesLabel,
    };
  } else {
    return {
      saveButtonDisabled: false,
      saveButtonLabel: props.saveFormLabel,
    };
  }
}



// WEBPACK FOOTER //
// ./src/js/requirements/components/RequirementViewBannerForm/RequirementViewBannerFormView.js